<template>
  <section class="material-grid">
    <!-- 顶部 Tab 区 -->
    <div class="material-grid__tab-bar">
      <div class="material-grid__tab-list">
        <button
          class="material-grid__tab material-grid__tab--primary"
          @click="onHotTabClick"
          :class="state.currentTab === 'hot' ? 'tab-active' : ''"
        >
          <i class="fa-solid fa-wand-magic-sparkles"></i> 一键同款
        </button>
      </div>
      <div class="material-grid__tab-aside">
        <button
          class="material-grid__tab material-grid__tab--secondary"
          @click="switchTab('my-videos')"
          :class="state.currentTab === 'my-videos' ? 'material-grid__tab--active' : ''"
        >
          <i class="fa-regular fa-folder-open material-grid__icon-leading"></i> 最近项目
        </button>
      </div>
    </div>

    <!-- 我的作品 (Session 列表) -->
    <template v-if="state.currentTab === 'my-videos'">
      <template v-if="state.sessionsLoading">
        <div class="material-grid__state">
          <div class="loader-ring material-grid__loader"></div>
          <p class="material-grid__muted-text">加载会话列表...</p>
        </div>
      </template>
      <template v-else-if="state.sessions.length === 0">
        <div class="material-grid__empty">
          <i class="fa-solid fa-film material-grid__empty-icon"></i>
          <p class="material-grid__empty-title">还没有生成过视频</p>
          <p class="material-grid__empty-hint">前往上方输入 Prompt，开始您的第一次 AI 创作吧 ✨</p>
        </div>
      </template>
      <template v-else>
        <div class="material-grid__session-grid">
          <div
            v-for="sess in state.sessions"
            :key="sess.id"
            class="material-grid__waterfall-card material-grid__session-card"
            @click="onSessionCardOpen(sess.id)"
          >
            <div class="material-grid__session-thumb">
              <template v-if="sess.latest_video_url">
                <div class="material-grid__cover-wrap">
                  <div v-if="!coverLoadedIds.has(sess.id)" class="material-grid__cover-loading">
                    <i class="fa-solid fa-spinner fa-spin"></i>
                    <span>加载中</span>
                  </div>
                  <video
                    :key="sess.latest_video_url"
                    :src="sess.latest_video_url"
                    class="material-grid__cover-media"
                    :class="{ 'material-grid__cover-media--loaded': coverLoadedIds.has(sess.id) }"
                    muted
                    preload="metadata"
                    @loadedmetadata="coverLoadedIds.add(sess.id)"
                    @mouseenter="($event.target as HTMLVideoElement)?.play()"
                    @mouseleave="
                      const v = $event.target as HTMLVideoElement;
                      v?.pause();
                      v && (v.currentTime = 0)
                    "
                  ></video>
                </div>
              </template>
              <template v-else-if="sess.latest_round_status === 'pending' || sess.latest_round_status === 'running'">
                <div class="material-grid__session-status">
                  <div class="material-grid__session-status-inner">
                    <i class="fa-solid fa-hourglass-half material-grid__status-icon material-grid__status-icon--muted"></i>
                    <p class="material-grid__status-label">生成中...</p>
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="material-grid__session-status">
                  <div class="material-grid__session-status-inner">
                    <i class="fa-solid fa-video material-grid__status-icon material-grid__status-icon--large"></i>
                    <p class="material-grid__status-label material-grid__status-label--secondary">
                      {{ sess.latest_round_status === 'failed' ? '已失败' : '无内容' }}
                    </p>
                  </div>
                </div>
              </template>
            </div>
            <div class="material-grid__session-footer">
              <p class="material-grid__session-title">
                {{ sess.title ? sess.title.substring(0, 15) : '无标题' }}...
              </p>
              <div class="material-grid__session-meta">
                <p class="material-grid__session-date">{{ formatSessionDate(sess.updated_at) }}</p>
                <button class="material-grid__session-delete" @click.stop="handleDeleteSession(sess.id)">
                  <i class="fa-solid fa-trash-can"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

      </template>
    </template>

    <!-- 素材区（一键同款） -->
    <template v-else>
      <!-- 加载中 -->
      <template v-if="state.pexelsLoading">
        <div class="material-grid__state">
          <div class="loader-ring material-grid__loader"></div>
          <p class="material-grid__muted-text">加载素材中...</p>
        </div>
      </template>

      <!-- 无数据 fallback -->
      <template v-else-if="state.pexelsData.length === 0">
        <div class="material-grid__header">
          <h2 class="material-grid__title">精选素材库</h2>
          <p class="material-grid__desc">快速上手做同款，提供丰富的灵感与同款配方。</p>
        </div>
        <div class="material-grid__masonry">
          <div v-for="(src, i) in fallbackImgs" :key="i" class="material-grid__waterfall-card">
            <img :src="src" class="material-grid__waterfall-img" loading="lazy" />
          </div>
        </div>
      </template>

      <!-- 正常素材卡片 - 瀑布流 -->
      <template v-else>
        <div class="material-grid__header">
          <h2 class="material-grid__title">精选素材库</h2>
          <p class="material-grid__desc">快速上手做同款，提供丰富的灵感与同款配方。</p>
        </div>

        <div class="material-grid__masonry">
          <div
            v-for="(v, i) in displayVideos"
            :key="'card-' + i"
            class="material-grid__waterfall-card group"
            @click="onHotMaterialCardClick(i)"
            @mouseenter="hoverPlayCard($event.currentTarget as HTMLElement, i, getVideoSrc(v))"
            @mouseleave="hoverStopCard($event.currentTarget as HTMLElement)"
          >
            <video
              v-if="!v.image && getVideoSrc(v)"
              :src="getVideoSrc(v)"
              class="material-grid__waterfall-img"
              muted
              preload="metadata"
            />
            <img
              v-else
              :src="v.image || v.video_pictures?.[0]?.picture || ''"
              class="material-grid__waterfall-img"
              loading="lazy"
            />
            <!-- 悬浮遮罩 -->
            <div class="material-grid__hover-overlay">
              <p class="material-grid__hover-prompt">{{ getPromptForVideo(i) }}</p>
              <div class="material-grid__hover-bottom">
                <button class="material-grid__hover-cta" @click.stop="onMakeSameFromCard(getPromptForVideo(i), v)">
                  <i class="fa-solid fa-wand-magic-sparkles material-grid__icon-leading"></i>做同款
                </button>
                <span class="material-grid__hover-duration">{{ v.duration ? v.duration + 's' : '5s' }}</span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="!allLoaded" ref="loadSentinel" class="material-grid__sentinel"></div>
        <p v-else class="material-grid__end-hint">已全部加载</p>
      </template>
    </template>
  </section>
</template>

<script setup lang="ts">
import { reactive, computed, ref, watch, onMounted, onUnmounted } from 'vue'
import { useAppStore } from '../composables/useAppStore'
import type { PexelsVideo } from '../types'
import { promptPools } from '../data/prompts'
import { reportClick } from '../util/report'

const { state, switchTab, openMaterialDetail, openSession, deleteSessionById, fallbackImgs, doMakeSameFromCard } = useAppStore()

const PAGE_SIZE = 8
const visibleCount = ref(PAGE_SIZE)
const loadSentinel = ref<HTMLElement | null>(null)

const displayVideos = computed(() => state.pexelsData.slice(0, visibleCount.value))
const allLoaded = computed(() => visibleCount.value >= state.pexelsData.length)

watch(
  () => state.pexelsData.length,
  () => {
    visibleCount.value = PAGE_SIZE
  }
)

let observer: IntersectionObserver | null = null

onMounted(() => {
  observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((e) => e.isIntersecting) && !allLoaded.value) {
        visibleCount.value = Math.min(visibleCount.value + PAGE_SIZE, state.pexelsData.length)
      }
    },
    { rootMargin: '200px' }
  )
  if (loadSentinel.value) observer.observe(loadSentinel.value)
})

watch(loadSentinel, (el, prev) => {
  if (!observer) return
  if (prev) observer.unobserve(prev)
  if (el) observer.observe(el)
})

onUnmounted(() => {
  observer?.disconnect()
  observer = null
})

function getPromptForVideo(index: number): string {
  const item = state.pexelsData[index]
  if (item?.prompt_text) return item.prompt_text
  const pool = promptPools[state.currentPexelsQuery] || promptPools['cinematic trending']
  return pool[index % pool.length]
}

function onHotTabClick(): void {
  reportClick('index', 'one_click_same')
  switchTab('hot')
}

function onSessionCardOpen(sessionId: string): void {
  reportClick('my_design', 'video_detail')
  void openSession(sessionId)
}

function onHotMaterialCardClick(index: number): void {
  reportClick('index', `hot_prompt_${index + 1}`)
  openMaterialDetail(index)
}

function onMakeSameFromCard(prompt: string, video: PexelsVideo): void {
  reportClick('index', 'create_same')
  doMakeSameFromCard(prompt, video)
}

const coverLoadedIds = reactive(new Set<string>())

function formatSessionDate(dateStr: string): string {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return d.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
}

async function handleDeleteSession(id: string): Promise<void> {
  const ok = await confirm('确定要删除这个会话吗？')
  if (ok) deleteSessionById(id)
}

function getVideoSrc(v: PexelsVideo): string {
  const file = v.video_files?.find((f) => f.quality === 'sd') || v.video_files?.[0]
  return file?.link || ''
}

function hoverPlayCard(container: HTMLElement, _index: number, videoSrc: string) {
  if (!videoSrc) return
  let video = container.querySelector('video.material-grid__hover-video') as HTMLVideoElement | null
  if (!video) {
    video = document.createElement('video')
    video.src = videoSrc
    video.muted = true
    video.loop = true
    video.playsInline = true
    video.className = 'material-grid__hover-video'
    video.style.pointerEvents = 'none'
    container.appendChild(video)
  }
  video.currentTime = 0
  video.style.display = 'block'
  video.play().catch(() => {})
}

function hoverStopCard(container: HTMLElement) {
  const video = container.querySelector('video.material-grid__hover-video') as HTMLVideoElement | null
  if (video) {
    video.pause()
    video.currentTime = 0
    video.style.display = 'none'
  }
}
</script>

<style scoped>
.material-grid {
  position: relative;
  z-index: 10;
  width: 100%;
  max-width: 90vw;
  margin-left: auto;
  margin-right: auto;
  padding: 1rem 2rem 5rem;
}

@media (min-width: 1536px) {
  .material-grid {
    max-width: 85vw;
  }
}

.material-grid__tab-bar {
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
}

.material-grid__tab-list {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 500;
  color: #6b7280;
}

.material-grid__tab-aside {
  margin-left: auto;
}

.material-grid__tab {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding-bottom: 0.25rem;
  transition:
    color 0.15s ease,
    background-color 0.15s ease,
    box-shadow 0.15s ease,
    opacity 0.15s ease,
    transform 0.15s ease;
  border: none;
  background: transparent;
  font: inherit;
}

.material-grid__tab--primary {
  gap: 0.25rem;
}

.material-grid__tab--secondary {
  font-weight: 700;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: #6b7280;
}

.material-grid__tab--secondary:hover {
  color: #2563eb;
}

.material-grid__tab--secondary.material-grid__tab--active {
  color: #2563eb;
}

.material-grid__icon-leading {
  margin-right: 0.375rem;
}

.material-grid__state {
  padding-top: 5rem;
  padding-bottom: 5rem;
  text-align: center;
}

.material-grid__loader {
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 1rem;
}

.material-grid__muted-text {
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: #9ca3af;
}

.material-grid__empty {
  padding-top: 5rem;
  padding-bottom: 5rem;
  text-align: center;
  color: #9ca3af;
}

.material-grid__empty-icon {
  display: block;
  margin-bottom: 1rem;
  font-size: 3rem;
  line-height: 1;
  opacity: 0.2;
}

.material-grid__empty-title {
  font-size: 1.125rem;
  line-height: 1.75rem;
  font-weight: 600;
  color: #6b7280;
}

.material-grid__empty-hint {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
}

.material-grid__session-grid {
  columns: 200px;
  column-gap: 1.25rem;
}

@media (min-width: 768px) {
  .material-grid__session-grid { columns: 240px; }
}

@media (min-width: 1280px) {
  .material-grid__session-grid { columns: 280px; }
}

@media (min-width: 1536px) {
  .material-grid__session-grid { columns: 320px; }
}

.material-grid__session-card {
  cursor: pointer;
  background-color: #fff;
  break-inside: avoid;
  margin-bottom: 1.25rem;
}

.material-grid__session-thumb {
  position: relative;
  overflow: hidden;
  background-color: #f3f4f6;
}

.material-grid__cover-wrap {
  position: relative;
  background-color: #f3f4f6;
  min-height: 120px;
}

.material-grid__cover-loading {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #f3f4f6;
  color: #9ca3af;
  font-size: 12px;
  z-index: 1;
}

.material-grid__cover-loading i {
  font-size: 20px;
}

.material-grid__session-thumb .material-grid__cover-media {
  width: 100%;
  height: auto;
  display: block;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.material-grid__session-thumb .material-grid__cover-media.material-grid__cover-media--loaded {
  opacity: 1;
}

.material-grid__session-status {
  aspect-ratio: 1 / 1;
}

.material-grid__cover-media {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.material-grid__session-play-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
}

.material-grid__play-circle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 9999px;
  background-color: rgb(255 255 255 / 0.8);
  backdrop-filter: blur(8px);
}

.material-grid__play-icon {
  color: #111827;
}

.material-grid__session-status {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.material-grid__session-status-inner {
  text-align: center;
}

.material-grid__status-icon {
  display: block;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 0.5rem;
  font-size: 1.5rem;
  line-height: 1;
  color: #d1d5db;
}

.material-grid__status-icon--muted {
  font-size: 1.25rem;
  color: #9ca3af;
}

.material-grid__status-icon--large {
  font-size: 1.875rem;
}

.material-grid__status-label {
  font-size: 0.75rem;
  line-height: 1rem;
  color: #6b7280;
}

.material-grid__status-label--secondary {
  color: #9ca3af;
}

.material-grid__session-footer {
  margin-top: auto;
  padding: 0.75rem;
  border-top: 1px solid #f9fafb;
}

.material-grid__session-title {
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 700;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.material-grid__session-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.25rem;
}

.material-grid__session-date {
  font-size: 10px;
  line-height: 1;
  color: #9ca3af;
}

.material-grid__session-delete {
  font-size: 10px;
  line-height: 1;
  color: #9ca3af;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  transition: color 0.15s ease;
}

.material-grid__session-delete:hover {
  color: #f87171;
}

.multi-select-bar__count {
  color: #fff;
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 500;
}

/* 瀑布流标题区 */
.material-grid__header {
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
}

.material-grid__title {
  font-size: 1.5rem;
  line-height: 2rem;
  font-weight: 800;
  color: #111827;
  margin-bottom: 0.5rem;
}

.material-grid__desc {
  font-size: 14px;
  color: #6b7280;
}

/* 瀑布流网格 */
.material-grid__masonry {
  columns: 2;
  column-gap: 1.25rem;
}

@media (min-width: 640px) {
  .material-grid__masonry { columns: 3; }
}

@media (min-width: 1024px) {
  .material-grid__masonry { columns: 4; }
}

@media (min-width: 1536px) {
  .material-grid__masonry { columns: 5; }
}

@media (min-width: 1920px) {
  .material-grid__masonry { columns: 6; }
}

.material-grid__waterfall-card {
  position: relative;
  break-inside: avoid;
  margin-bottom: 1.5rem;
  border-radius: 1rem;
  overflow: hidden;
  cursor: pointer;
  background-color: #f3f4f6;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.material-grid__waterfall-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1);
}

.material-grid__waterfall-img {
  width: 100%;
  height: auto;
  display: block;
  transition: transform 0.7s ease;
}

.material-grid__waterfall-card:hover .material-grid__waterfall-img {
  transform: scale(1.05);
}

/* 悬浮遮罩 */
.material-grid__hover-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.2) 40%, transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 1rem;
  z-index: 2;
  pointer-events: none;
}

.material-grid__waterfall-card:hover .material-grid__hover-overlay {
  opacity: 1;
  pointer-events: auto;
}

.material-grid__hover-prompt {
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  margin-bottom: 0.75rem;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
}

.material-grid__hover-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.material-grid__hover-cta {
  padding: 0.5rem 1rem;
  background: #2563eb;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.material-grid__hover-cta:hover {
  background: #3b82f6;
}

.material-grid__hover-duration {
  color: rgba(255, 255, 255, 0.9);
  font-size: 10px;
  font-weight: 700;
  backdrop-filter: blur(8px);
  background: rgba(0, 0, 0, 0.4);
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.material-grid__sentinel {
  height: 1px;
  margin-top: 1.5rem;
}

.material-grid__end-hint {
  margin-top: 1.5rem;
  text-align: center;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: #9ca3af;
}

:global(.material-grid__hover-video) {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
}
</style>
